import type { TrackFileEntry } from '../tracks/index'
import { ConfigError, formatIssues } from './errors'
import type { Balance, HazardBalance } from './loadConfig'
import { trackSchema } from './track.schema'
import type { TrackDef } from './track.schema'

/**
 * Validate every track file and cross-check hazard timing against the balance. Throws one
 * `ConfigError` listing every issue as `tracks.<file>.<path>: message`.
 */
export function loadTracks(
  files: readonly TrackFileEntry[],
  balance: Balance,
): ReadonlyMap<string, TrackDef> {
  const tracks = new Map<string, TrackDef>()
  const problems: string[] = []
  for (const { file, json } of files) {
    const prefix = `tracks.${file}`
    const parsed = trackSchema.safeParse(json)
    if (!parsed.success) {
      problems.push(formatIssues(prefix, parsed.error))
      continue
    }
    const track = parsed.data
    if (track.id !== file) problems.push(`${prefix}.id: must equal the file name (${file})`)
    if (tracks.has(track.id)) problems.push(`${prefix}.id: duplicate track id ${track.id}`)
    problems.push(...hazardTimingIssues(track, balance).map((m) => `${prefix}.${m}`))
    tracks.set(track.id, track)
  }
  if (problems.length > 0) throw new ConfigError(`Invalid config\n${problems.join('\n')}`)
  return tracks
}

/** Ticks a `barrel_roll` barrel rolls: from the gate at `x + w` until its right edge passes `x`. */
export function barrelRollTicks(w: number, h: HazardBalance): number {
  return Math.ceil(w / h.barrelSpeedPxPerTick)
}

/** A periodic hazard's cycle must fit its telegraph plus its active or rest window, with time left. */
function hazardTimingIssues(track: TrackDef, balance: Balance): string[] {
  const h = balance.hazards
  const issues: string[] = []
  track.segments.forEach((s, i) => {
    if (s.type === 'spike_strip') {
      const used = s.activeTicks + h.spikeTelegraphTicks
      if (used >= s.periodTicks) {
        issues.push(`segments.${i}.periodTicks: must exceed activeTicks + telegraph (${used})`)
      }
    }
    if (s.type === 'trapdoor') {
      const used = h.trapdoorSprungTicks + h.trapdoorTelegraphTicks
      if (used >= s.periodTicks) {
        issues.push(`segments.${i}.periodTicks: must exceed sprung + telegraph (${used})`)
      }
    }
    if (s.type === 'barrel_roll') {
      const used = h.barrelTelegraphTicks + barrelRollTicks(s.w, h)
      if (used > s.periodTicks) {
        issues.push(`segments.${i}.periodTicks: must be at least telegraph + roll (${used})`)
      }
    }
    if (s.type === 'falling_block' || s.type === 'boulder_drop') {
      const used = h.fallingBlockTelegraphTicks + h.fallingBlockRestTicks
      if (used >= s.periodTicks) {
        issues.push(`segments.${i}.periodTicks: must exceed telegraph + rest (${used}) + fall`)
      }
    }
  })
  return issues
}
